import { useRef, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Upload, Download, Lock, FileText, Image, Link2, File, Eye } from 'lucide-react';
import type { FileAsset } from '@/types';

interface AssetExchangeProps {
  files: FileAsset[];
  onUpload?: (files: FileList) => Promise<void> | void;
  onDownload?: (file: FileAsset) => Promise<void> | void;
}

export function AssetExchange({ files, onUpload, onDownload }: AssetExchangeProps) {
  const [filter, setFilter] = useState<FileAsset['type'] | 'all'>('all');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filteredFiles = filter === 'all' ? files : files.filter(f => f.type === filter);

  const fileTypeConfig = {
    design: { icon: Image, label: 'Design Files', color: 'text-purple-600 bg-purple-50' },
    staging: { icon: Link2, label: 'Staging Links', color: 'text-blue-600 bg-blue-50' },
    credentials: { icon: Lock, label: 'Credentials', color: 'text-amber-600 bg-amber-50' },
    export: { icon: File, label: 'Exports', color: 'text-green-600 bg-green-50' },
    deliverable: { icon: FileText, label: 'Deliverables', color: 'text-slate-600 bg-slate-50' },
  };

  const isSafeExternalUrl = (url: string) => {
    try {
      return ['http:', 'https:'].includes(new URL(url).protocol);
    } catch {
      return false;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length || !onUpload) return;
    setIsUploading(true);
    await onUpload(event.target.files);
    setIsUploading(false);
    event.target.value = '';
  };

  return (
    <Card 
      title="File & Asset Exchange" 
      subtitle="Centralized place for design files, staging links, and deliverables"
      action={
        <Button
          size="sm"
          leftIcon={<Upload className="w-4 h-4" />}
          isLoading={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload
        </Button>
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      <div className="space-y-4">
        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All Files
          </button>
          {(['design', 'staging', 'credentials', 'deliverable'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                filter === type
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {fileTypeConfig[type].label}
            </button>
          ))}
        </div>

        {/* File list */}
        <div className="space-y-2">
          {filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <File className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No files found</p>
            </div>
          ) : (
            filteredFiles.map((file) => {
              const config = fileTypeConfig[file.type];
              const Icon = config.icon;

              return (
                <div
                  key={file.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:border-blue-300 hover:shadow-sm transition-all group"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate group-hover:text-blue-600 transition-colors">
                      {file.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="capitalize">{file.type}</span>
                      <span>•</span>
                      <span>{file.size}</span>
                      <span>•</span>
                      <span>{file.uploadedBy}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {file.type === 'staging' && isSafeExternalUrl(file.url) && !file.url.includes('/api/files/') ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onDownload?.(file)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </Card>
  );
}
